import React, { useState, useEffect } from 'react';
import { S } from '../styles';
import { getTheme } from '../theme';
import { API, authHeaders } from '../utils/helpers';
import TicketCard from './TicketCard';

const DEFAULT_SENDBACK_REASONS = [
  'More information needed',
  'Missing parts or labor entries',
  'Customer not yet contacted',
  'Incorrect diagnosis — please re-evaluate',
  'Waiting on parts — update status',
  'Estimate not approved yet',
];



export default function ManagerReviewWizard({ tickets, allStatuses, theme, onClose, onTicketUpdated, shopProfile, currentUser = null }) {
  const T = getTheme(theme);
  const isDark = theme === 'dark';

  const [queue] = useState(() => [...tickets].sort((a, b) => b.riskScore - a.riskScore));
  const [idx, setIdx] = useState(0);
  const [action, setAction] = useState(null);
  const [sendbackReason, setSendbackReason] = useState('');
  const [sendbackNote, setSendbackNote] = useState('');
  const [noteText, setNoteText] = useState('');
  const [noteStatusId, setNoteStatusId] = useState('');
  const [noteTechId, setNoteTechId] = useState('');
  const [finalStatus, setFinalStatus] = useState('');
  const [finalNote, setFinalNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [savedMsg, setSavedMsg] = useState('');
  const [ticketDetail, setTicketDetail] = useState(null);
  const [payments, setPayments] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [sendbackReasons, setSendbackReasons] = useState(DEFAULT_SENDBACK_REASONS);
  const [employees, setEmployees] = useState([]);

  const ticket = queue[idx] || null;
  const total = queue.length;
  const isLast = idx >= total - 1;
  const managerName = currentUser?.first_name
    ? `${currentUser.first_name} ${currentUser.last_name || ''}`.trim()
    : (shopProfile?.managerName || shopProfile?.contactName || 'Manager');

  useEffect(() => {
    fetch(`${API}/api/settings`, { credentials:'include', headers: authHeaders() })
      .then(r => r.json())
      .then(data => { if (data?.sendbackReasons?.length) setSendbackReasons(data.sendbackReasons); })
      .catch(() => {});
    fetch(`${API}/api/tickets/employees/list`, { credentials:'include', headers: authHeaders() })
      .then(r => r.json())
      .then(data => { if (Array.isArray(data)) setEmployees(data); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!ticket) return;
    setLoadingDetail(true);
    setTicketDetail(null);
    setPayments(null);
    setAction(null);
    setSendbackReason(''); setSendbackNote('');
    setNoteText(''); setNoteStatusId(''); setNoteTechId('');
    setFinalStatus(''); setFinalNote('');
    fetch(`${API}/api/tickets/${ticket.id}`, { credentials:'include', headers: authHeaders() })
      .then(r => r.json())
      .then(data => { setTicketDetail(data); setLoadingDetail(false); })
      .catch(() => setLoadingDetail(false));

    // Payment history for the rail — Spinboard's own Postgres, not Lightspeed.
    // Defaults to [] on failure so the rail reads "None requested" rather than
    // disappearing, and never blocks the review flow.
    fetch(`${API}/api/payments/list?workorderId=${encodeURIComponent(ticket.id)}`, {
      credentials:'include',
      headers: { Authorization: `Bearer ${localStorage.getItem('sb_token')}` },
    })
      .then(r => r.ok ? r.json() : { requests: [] })
      .then(d => setPayments(d.requests || []))
      .catch(() => setPayments([]));
  }, [ticket?.id]);

  const buildTimestamp = () => {
    const now = new Date();
    const dateStr = now.toLocaleDateString('en-US', { month:'short', day:'numeric', year:'numeric' });
    const timeStr = now.toLocaleTimeString('en-US', { hour:'numeric', minute:'2-digit' });
    return `[Manager Review — ${managerName} — ${dateStr} ${timeStr}]`;
  };

  const writeInternalNote = async (ticketId, noteToAppend, existingNote) => {
    const timestamp = buildTimestamp();
    const newEntry = `${timestamp}\n${noteToAppend.trim()}`;
    const combined = existingNote?.trim()
      ? `${newEntry}\n\n${existingNote.trim()}`
      : newEntry;
    await fetch(`${API}/api/tickets/${ticketId}/internal-note`, {
      method:'PUT', credentials:'include',
      headers: authHeaders(true),
      body: JSON.stringify({ note: combined }),
    });
    return combined;
  };

  const advance = (msg) => {
    setSavedMsg(msg);
    setTimeout(() => {
      setSavedMsg('');
      if (isLast) { onClose(); }
      else { setIdx(i => i + 1); }
    }, 1200);
  };

  const handleSendBack = async () => {
    if (!sendbackReason) return;
    setSaving(true);
    try {
      const noteContent = sendbackNote.trim()
        ? `Sent back: ${sendbackReason}\n${sendbackNote.trim()}`
        : `Sent back: ${sendbackReason}`;
      await writeInternalNote(ticket.id, noteContent, ticketDetail?.internalNote);
      const openStatus = allStatuses.find(s => s.name.toLowerCase() === 'open');
      if (openStatus) {
        await fetch(`${API}/api/tickets/${ticket.id}/status`, {
          method:'PUT', credentials:'include',
          headers:{'Content-Type':'application/json', Authorization:`Bearer ${localStorage.getItem('sb_token')}`},
          // sendBack: recorded in the ticket's history with the reason, and the
          // tech's Workbench card goes back to In Progress (ticketEvents).
          body: JSON.stringify({ workorderStatusID: openStatus.id, clearCompletion: true,
            sendBack: { reason: sendbackReason, note: sendbackNote.trim() } }),
        });
      }
      if (onTicketUpdated) onTicketUpdated(ticket.id, { status: 'Open' });
      advance('↩ Sent back to tech');
    } catch (err) { console.error(err); }
    setSaving(false);
  };

  const handleAddNote = async () => {
    if (!noteText.trim()) return;
    setSaving(true);
    try {
      await writeInternalNote(ticket.id, noteText, ticketDetail?.internalNote);
      if (noteStatusId) {
        await fetch(`${API}/api/tickets/${ticket.id}/status`, {
          method:'PUT', credentials:'include',
          headers:{'Content-Type':'application/json', Authorization:`Bearer ${localStorage.getItem('sb_token')}`},
          body: JSON.stringify({ workorderStatusID: noteStatusId }),
        });
      }
      if (noteTechId) {
        await fetch(`${API}/api/tickets/${ticket.id}/tech`, {
          method:'PUT', credentials:'include',
          headers: authHeaders(true),
          body: JSON.stringify({ employeeID: noteTechId }),
        });
      }
      const statusName = noteStatusId ? allStatuses.find(s => s.id === noteStatusId)?.name : undefined;
      const techName = noteTechId ? employees.find(e => e.id === noteTechId)?.name : undefined;
      if (onTicketUpdated) onTicketUpdated(ticket.id, {
        ...(statusName ? { status: statusName } : {}),
        ...(techName ? { tech: techName } : {}),
      });
      advance('✓ Note saved');
    } catch (err) { console.error(err); }
    setSaving(false);
  };

  const handleFinalize = async () => {
    if (!finalStatus) return;
    setSaving(true);
    try {
      if (finalNote.trim()) {
        await writeInternalNote(ticket.id, `Finalized: ${finalNote.trim()}`, ticketDetail?.internalNote);
      }
      await fetch(`${API}/api/tickets/${ticket.id}/status`, {
        method:'PUT', credentials:'include',
        headers:{'Content-Type':'application/json', Authorization:`Bearer ${localStorage.getItem('sb_token')}`},
        body: JSON.stringify({ workorderStatusID: finalStatus }),
      });
      const statusName = allStatuses.find(s => s.id === finalStatus)?.name || 'Finished';
      if (onTicketUpdated) onTicketUpdated(ticket.id, { status: statusName });
      advance('✓ Finalized');
    } catch (err) { console.error(err); }
    setSaving(false);
  };

  const finishStatuses = allStatuses.filter(s => {
    const n = s.name.toLowerCase();
    return ['finished','done & paid','finished/paid'].includes(n) && !(n.includes('done') && n.includes('paid'));
  });

  const inputStyle = {
    width:'100%', background:T.input, border:`1px solid ${T.inputBorder}`,
    borderRadius:8, padding:'10px 14px', color:T.text, fontSize:14,
    fontFamily:'Arial, sans-serif', resize:'vertical', outline:'none',
    boxSizing:'border-box', lineHeight:1.6,
  };

  const labelStyle = { fontSize:11, color:T.textSub, textTransform:'uppercase', letterSpacing:'0.06em', fontWeight:600 };
  const cardStyle = {
    background: isDark ? '#141d30' : '#fbfbf7',
    border:`1px solid ${isDark ? '#243050' : '#e0e0d8'}`,
    borderRadius:10,
  };

  
  if (total === 0) {
    return (
      <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.75)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center' }}>
        <div style={{ background:T.card, borderRadius:14, padding:'56px 48px', textAlign:'center', maxWidth:440 }}>
          <div style={{ fontSize:56, marginBottom:16 }}>✓</div>
          <div style={{ fontSize:22, fontWeight:700, color:T.text, marginBottom:8 }}>Queue is clear!</div>
          <div style={{ fontSize:15, color:T.textSub, marginBottom:28 }}>No tickets pending manager review.</div>
          <button onClick={onClose} style={{ ...S.btn(true), background:'#9b59b6', color:'#fff', padding:'12px 32px', fontSize:14 }}>Close</button>
        </div>
      </div>
    );
  }

  if (!ticket) return null;

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.8)', zIndex:1000, display:'flex', alignItems:'flex-start', justifyContent:'center', overflowY:'auto', padding:'24px 16px' }}>
      <div style={{ width:'100%', maxWidth:1400, minWidth:760, background:T.bg, borderRadius:16, overflow:'hidden', boxShadow:'0 32px 80px rgba(0,0,0,0.5)', marginBottom:24, resize:'both' }}>

        {/* ── Header ── */}
        <div style={{ background: isDark ? '#161f30' : '#ebebE3', padding:'18px 28px', display:'flex', alignItems:'center', gap:20, borderBottom:`1px solid ${T.border}` }}>
          <div style={{ flex:1 }}>
            <div style={{ fontSize:11, color:'#9b59b6', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.12em', marginBottom:4 }}>Manager Review Wizard</div>
            <div style={{ fontSize:14, color: isDark ? '#8a99b8' : T.textSub }}>
              Ticket <strong style={{ color:T.text, fontSize:16 }}>{idx + 1}</strong>
              <span style={{ color: isDark ? '#8a99b8' : T.textSub }}> of </span>
              <strong style={{ color:T.text, fontSize:16 }}>{total}</strong>
            </div>
          </div>
          {/* Progress dots */}
          <div style={{ display:'flex', gap:8, alignItems:'center' }}>
            {queue.map((_, i) => (
              <div key={i}
                onClick={() => i <= idx && setIdx(i)}
                title={i < idx ? `Go back to ticket ${i+1}` : i === idx ? `Current` : ''}
                style={{ width: i === idx ? 28 : 10, height:10, borderRadius:5, transition:'all 0.2s',
                  cursor: i <= idx ? 'pointer' : 'default',
                  background: i < idx ? '#2ecc71' : i === idx ? '#9b59b6' : (isDark ? '#2e3d58' : '#d0d0c8') }} />
            ))}
          </div>
          {/* Progress bar */}
          <div style={{ width:160 }}>
            <div style={{ height:6, borderRadius:3, background: isDark ? '#243050' : '#d8d8d0', overflow:'hidden' }}>
              <div style={{ height:'100%', borderRadius:3, width:`${((idx + 1) / total) * 100}%`, background:'#9b59b6', transition:'width 0.4s' }} />
            </div>
            <div style={{ fontSize:11, color:T.textSub, marginTop:4, textAlign:'right' }}>
              {Math.round(((idx + 1) / total) * 100)}% complete
            </div>
          </div>
          <button onClick={onClose}
            style={{ background:'transparent', border:`1px solid ${T.border}`, borderRadius:8, color:T.textSub, cursor:'pointer', padding:'8px 16px', fontSize:13 }}>
            Exit
          </button>
        </div>

        {/* ── Body — single column ── */}
        <div style={{ padding:'20px 28px', display:'flex', flexDirection:'column', gap:14 }}>

<TicketCard theme={theme} ticket={ticket} ticketDetail={ticketDetail} loadingDetail={loadingDetail} payments={payments} />

          {/* ═══ Bottom action bar ═══ */}
          <div style={{ ...cardStyle, padding:'16px 20px' }}>

            {/* Success flash */}
            {savedMsg && (
              <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:14, padding:'12px 0' }}>
                <div style={{ fontSize:36 }}>{savedMsg.includes('↩') ? '↩' : '✓'}</div>
                <div>
                  <div style={{ fontSize:18, fontWeight:700,
                    color: savedMsg.includes('↩') ? '#e67e22' : '#2ecc71' }}>{savedMsg}</div>
                  <div style={{ fontSize:13, color:T.textSub }}>{isLast ? 'Completing review…' : 'Moving to next ticket…'}</div>
                </div>
              </div>
            )}

            {/* Action picker row */}
            {!action && !savedMsg && (
              <div style={{ display:'flex', alignItems:'center', gap:12, flexWrap:'wrap' }}>
                <div style={{ fontSize:13, color:T.textSub, fontWeight:500, marginRight:'auto' }}>What would you like to do?</div>
                {[
                  { id:'sendback', icon:'↩', label:'Send Back to Tech', color:'#e74c3c', hoverBg: isDark ? '#2a0f0f' : '#fde8e8' },
                  { id:'note',     icon:'📝', label:'Add Manager Note', color:'#3498db', hoverBg: isDark ? '#0f1e30' : '#e8f0f8' },
                  { id:'finalize', icon:'✓', label:'Finalize Ticket', color:'#2ecc71', hoverBg: isDark ? '#0f2a15' : '#d4edda' },
                ].map(opt => (
                  <button key={opt.id} onClick={() => setAction(opt.id)}
                    style={{ padding:'12px 18px', borderRadius:10, border:`2px solid ${isDark ? '#2e3d58' : '#d0d0c8'}`,
                      background:'transparent', cursor:'pointer', transition:'all 0.15s',
                      display:'flex', alignItems:'center', gap:10 }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = opt.color; e.currentTarget.style.background = opt.hoverBg; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = isDark ? '#2e3d58' : '#d0d0c8'; e.currentTarget.style.background = 'transparent'; }}>
                    <span style={{ fontSize:20, lineHeight:1, color: opt.color }}>{opt.icon}</span>
                    <span style={{ fontSize:14, fontWeight:600, color:T.text }}>{opt.label}</span>
                  </button>
                ))}
                <div style={{ width:1, height:32, background: isDark ? '#243050' : '#d8d8d0' }} />
                <button onClick={() => { if (isLast) onClose(); else setIdx(i => i + 1); }}
                  style={{ ...S.btn(false), fontSize:13, color:T.textSub, borderColor:T.border, padding:'12px 20px' }}>
                  Skip for now →
                </button>
              </div>
            )}

            {/* Send back form */}
            {action === 'sendback' && !savedMsg && (
              <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
                <div style={{ fontSize:15, fontWeight:700, color:'#e74c3c' }}>↩ Send Back to Tech</div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16 }}>
                  <div>
                    <div style={{ ...labelStyle, marginBottom:8 }}>Select a reason</div>
                    <div style={{ display:'grid', gap:6 }}>
                      {sendbackReasons.map(r => (
                        <label key={r} style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 14px', borderRadius:8,
                          background: sendbackReason === r ? (isDark ? '#2a1a0a' : '#fef3e2') : (isDark ? '#1e2a40' : '#f4f4ef'),
                          border:`1px solid ${sendbackReason === r ? '#e67e22' : T.border}`,
                          cursor:'pointer', transition:'all 0.15s' }}>
                          <input type="radio" name="reason" value={r} checked={sendbackReason === r}
                            onChange={() => setSendbackReason(r)}
                            style={{ accentColor:'#e67e22', cursor:'pointer', width:16, height:16 }} />
                          <span style={{ fontSize:13, color:T.text }}>{r}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                    <div style={labelStyle}>Additional note (optional)</div>
                    <textarea value={sendbackNote} onChange={e => setSendbackNote(e.target.value)}
                      placeholder="Add more detail for the tech…" rows={6}
                      style={{ ...inputStyle, flex:1 }} />
                    <div style={{ display:'flex', gap:10 }}>
                      <button onClick={handleSendBack} disabled={!sendbackReason || saving}
                        style={{ ...S.btn(true), background: sendbackReason ? '#e74c3c' : (isDark ? '#243050' : '#d0d0c8'),
                          color: sendbackReason ? '#fff' : T.textSub, padding:'12px 24px', fontSize:14,
                          cursor: sendbackReason ? 'pointer' : 'not-allowed', opacity: saving ? 0.7 : 1, flex:1 }}>
                        {saving ? 'Saving…' : '↩ Send Back & Next'}
                      </button>
                      <button onClick={() => setAction(null)} style={{ ...S.btn(false), color:T.textSub, borderColor:T.border, padding:'12px 18px' }}>Back</button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Add note form */}
            {action === 'note' && !savedMsg && (
              <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
                <div style={{ fontSize:15, fontWeight:700, color:'#3498db' }}>📝 Add Manager Note</div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16 }}>
                  <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                    <div style={labelStyle}>Note (saved to internal notes)</div>
                    <textarea value={noteText} onChange={e => setNoteText(e.target.value)} autoFocus
                      placeholder="Enter your notes or concerns…" rows={6}
                      style={{ ...inputStyle, border:`1px solid ${noteText.trim() ? '#3498db' : T.inputBorder}`, flex:1 }} />
                    <div style={{ fontSize:11, color:T.textSub }}>
                      Timestamped as: <em style={{ color: isDark ? '#5a8ab8' : '#666' }}>{buildTimestamp()}</em>
                    </div>
                  </div>
                  <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
                    <div>
                      <div style={{ ...labelStyle, marginBottom:6 }}>Update Status (optional)</div>
                      <select value={noteStatusId} onChange={e => setNoteStatusId(e.target.value)}
                        style={{ width:'100%', background:T.input, border:`1px solid ${noteStatusId ? '#3498db' : T.inputBorder}`,
                          borderRadius:8, padding:'8px 12px', color:T.text, fontSize:13,
                          fontFamily:'Arial, sans-serif', outline:'none', cursor:'pointer' }}>
                        <option value="">— Keep current ({ticket.status}) —</option>
                        {allStatuses.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                      </select>
                    </div>
                    <div>
                      <div style={{ ...labelStyle, marginBottom:6 }}>Reassign Tech (optional)</div>
                      <select value={noteTechId} onChange={e => setNoteTechId(e.target.value)}
                        style={{ width:'100%', background:T.input, border:`1px solid ${noteTechId ? '#3498db' : T.inputBorder}`,
                          borderRadius:8, padding:'8px 12px', color:T.text, fontSize:13,
                          fontFamily:'Arial, sans-serif', outline:'none', cursor:'pointer' }}>
                        <option value="">— Keep current ({ticket.tech}) —</option>
                        {employees.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
                      </select>
                    </div>
                    <div style={{ display:'flex', gap:10, marginTop:'auto' }}>
                      <button onClick={handleAddNote} disabled={!noteText.trim() || saving}
                        style={{ ...S.btn(true), background: noteText.trim() ? '#3498db' : (isDark ? '#243050' : '#d0d0c8'),
                          color: noteText.trim() ? '#fff' : T.textSub, padding:'12px 24px', fontSize:14,
                          cursor: noteText.trim() ? 'pointer' : 'not-allowed', opacity: saving ? 0.7 : 1, flex:1 }}>
                        {saving ? 'Saving…' : '✓ Save Note & Next'}
                      </button>
                      <button onClick={() => setAction(null)} style={{ ...S.btn(false), color:T.textSub, borderColor:T.border, padding:'12px 18px' }}>Back</button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Finalize form */}
            {action === 'finalize' && !savedMsg && (
              <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
                <div style={{ fontSize:15, fontWeight:700, color:'#2ecc71' }}>✓ Finalize Ticket</div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16 }}>
                  <div>
                    <div style={{ ...labelStyle, marginBottom:8 }}>Set final status</div>
                    <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                      {finishStatuses.length > 0 ? finishStatuses.map(s => (
                        <button key={s.id} onClick={() => setFinalStatus(s.id)}
                          style={{ padding:'10px 20px', borderRadius:8, cursor:'pointer', fontSize:14, fontWeight:500,
                            border:`2px solid ${finalStatus === s.id ? '#2ecc71' : T.border}`,
                            background: finalStatus === s.id ? (isDark ? '#0f2a15' : '#d4edda') : 'transparent',
                            color: finalStatus === s.id ? '#2ecc71' : T.text, transition:'all 0.15s' }}>
                          {s.name}
                        </button>
                      )) : (
                        <div style={{ fontSize:13, color:'#e74c3c' }}>No finish statuses found — check Settings status categories.</div>
                      )}
                    </div>
                  </div>
                  <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                    <div style={labelStyle}>Final note (optional)</div>
                    <textarea value={finalNote} onChange={e => setFinalNote(e.target.value)}
                      placeholder="Any final notes for the record…" rows={5}
                      style={{ ...inputStyle, flex:1 }} />
                    <div style={{ display:'flex', gap:10 }}>
                      <button onClick={handleFinalize} disabled={!finalStatus || saving}
                        style={{ ...S.btn(true), background: finalStatus ? '#2ecc71' : (isDark ? '#243050' : '#d0d0c8'),
                          color: finalStatus ? '#fff' : T.textSub, padding:'12px 24px', fontSize:14,
                          cursor: finalStatus ? 'pointer' : 'not-allowed', opacity: saving ? 0.7 : 1, flex:1 }}>
                        {saving ? 'Saving…' : `✓ Finalize & ${isLast ? 'Finish' : 'Next'}`}
                      </button>
                      <button onClick={() => setAction(null)} style={{ ...S.btn(false), color:T.textSub, borderColor:T.border, padding:'12px 18px' }}>Back</button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}